import React, { useEffect, useMemo, useRef, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import ErrorBoundary from "@/components/ErrorBoundary";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { RUN_META } from "@/lib/format";
import { useSessionState } from "@/hooks/useSessionState";
import {
  Icon, IconButton, Kbd, Avatar, RoleChip, VersionChip, LogoMark, DataStateBadge, SystemBadge, EmptyState, ConfirmModal, cx,
  usePrefs, useViewport, useNow, timeET, timeSYD, dayLabel, sessionDateLabel, fmtDuration, THEME_LABELS, etAndLocal, timeLocal, localZone, localZoneAbbr, withLocalTimes } from "@/sentinel";

const APP_VERSION = process.env.REACT_APP_VERSION || null;

/* ---------- IA ---------- */
// `min` mirrors the route guards in App.js; the backend remains the authority.
// `hidden` items keep their shortcut and palette entry but are not drawn in the rail
// (Primary and Candidates are sections of the Tonight session desk).
// `hideLocked` items are left out entirely for roles that cannot open them, instead of showing a lock.
export const NAV = [
  { group: "Session", fixed: true, items: [
    { to: "/tonight", label: "Tonight", icon: "LayoutDashboard", key: "T" },
    { to: "/checkpoints", label: "Runs", icon: "GitCommitVertical", key: "K" },
    { to: "/tonight#desk-ticket", label: "Ticket or blocking rule (Tonight)", icon: "Crosshair", key: "P", hidden: true },
    { to: "/tonight#desk-candidates", label: "Ranked candidates (Tonight)", icon: "ListOrdered", key: "C", hidden: true },
  ] },
  { group: "Evidence", items: [
    { to: "/sources", label: "Sources", icon: "Database", key: "S", flag: "prov" },
    { to: "/scoring", label: "Scoring", icon: "Target" },
    { to: "/fills", label: "Fill journal", icon: "ClipboardCheck", testId: "nav-fills" },
    { to: "/history", label: "History", icon: "History" },
    { to: "/charts", label: "Charts", icon: "ChartCandlestick", key: "H" },
  ] },
  { group: "Research", items: [
    { to: "/backtest", label: "Backtest", icon: "FlaskConical", min: "analyst" },
  ] },
  { group: "Configure", items: [
    { to: "/thresholds", label: "Old rule settings", icon: "SlidersHorizontal", min: "analyst" },
    { to: "/admin", label: "Admin", icon: "ShieldAlert", min: "analyst" },
  ] },
  { group: "Other desks", items: [
    { to: "/equities", label: "Side experiments", icon: "ScanSearch", key: "E", testId: "nav-equities", switcher: true },
    { to: "/megacap", label: "MegaCap playbook", icon: "Layers", key: "M", testId: "nav-megacap", switcher: true },
    { to: "/pm", label: "Project", icon: "SquareKanban", testId: "open-pm-btn", min: "owner", hideLocked: true, switcher: true },
  ] },
  { group: "Help", items: [
    { to: "/guide", label: "Guide & tour", icon: "BookOpen", testId: "nav-guide" },
  ] },
];

/** The equities desk: a separate workspace with its own rail and accent, not part of the SPX decision flow. */
export const EQ_NAV = [
  // Owner decision 2026-10-14 (the 10:00 am programme is primary): the swing scanner's Setups, Discover and
  // Tape are hidden (every test of the scanner found no edge); their routes still work by URL. What remains
  // are the paper records of ideas outside the main programme.
  { group: "Side experiments", fixed: true, items: [
    { to: "/equities/cef", label: "Fund discounts", icon: "Scale", testId: "equities-nav-cef" },
    { to: "/equities/spacs", label: "SPAC trust values", icon: "Hourglass", testId: "equities-nav-spacs" },
    { to: "/equities/tenders", label: "Tender offers", icon: "FileText", testId: "equities-nav-tenders" },
    { to: "/equities/track-record", label: "Track record", icon: "History", testId: "equities-nav-track-record" },
    { to: "/equities/journal", label: "Journal", icon: "BookOpen", testId: "equities-nav-journal" },
    { to: "/equities/watchlist", label: "Watchlist", icon: "ListChecks", testId: "equities-nav-watchlist" },
  ] },
  { group: "Other desks", items: [
    { to: "/tonight", label: "SPX session desk", icon: "LayoutDashboard", testId: "eq-back-to-spx", switcher: true },
    { to: "/megacap", label: "MegaCap playbook", icon: "Layers", switcher: true },
    { to: "/pm", label: "Project", icon: "SquareKanban", min: "owner", hideLocked: true, switcher: true },
  ] },
  { group: "Help", items: [
    { to: "/guide", label: "Guide & tour", icon: "BookOpen", testId: "nav-guide" },
  ] },
];

export const PM_NAV = [
  { group: "Project", fixed: true, items: [
    { to: "/pm", end: true, label: "Board", icon: "SquareKanban", testId: "pm-nav-board" },
    { to: "/pm/activity", label: "Activity", icon: "Activity", testId: "pm-nav-activity" },
    { to: "/pm/decisions", label: "Decisions log", icon: "Gavel", testId: "pm-nav-decisions" },
    { to: "/pm/discussions", label: "Discussions", icon: "MessageSquare", testId: "pm-nav-discussions" },
    { to: "/pm/milestones", label: "Roadmap", icon: "Map", testId: "pm-nav-roadmap" },
    { to: "/pm/digest", label: "Weekly digest", icon: "Newspaper", testId: "pm-nav-weekly digest" },
  ] },
  { group: "Operations", items: [
    { to: "/pm/costs", label: "Costs", icon: "DollarSign", testId: "pm-nav-costs" },
    { to: "/pm/timelog", label: "Time log", icon: "Timer", testId: "pm-nav-time log" },
    { to: "/pm/budget", label: "Budget", icon: "Wallet", testId: "pm-nav-budget" },
    { to: "/pm/prompts", label: "Prompt library", icon: "FileText", testId: "pm-nav-prompt library" },
    { to: "/pm/context", label: "Handoff context", icon: "Handshake", testId: "pm-nav-context / handoff" },
    { to: "/pm/notifications", label: "Notifications", icon: "BellRing", testId: "pm-nav-notifications", flag: "pm-unread" },
    { to: "/pm/members", label: "Members", icon: "Users", testId: "pm-nav-members" },
  ] },
  { group: "Other desks", items: [
    { to: "/tonight", label: "SPX session desk", icon: "LayoutDashboard", testId: "pm-back-to-sentinel-top", switcher: true },
    { to: "/equities", label: "Side experiments", icon: "ScanSearch", switcher: true },
    { to: "/megacap", label: "MegaCap playbook", icon: "Layers", switcher: true },
  ] },
];

/** The MegaCap playbook stream (roadmap Phase 7): its own rail and accent, separate from the 10:00 am programme. */
export const MC_NAV = [
  { group: "MegaCap playbook", fixed: true, items: [
    { to: "/megacap", end: true, label: "Test register", icon: "ListChecks", testId: "megacap-nav-register" },
    { to: "/megacap/chapters", label: "Playbook map", icon: "BookOpen", testId: "megacap-nav-chapters" },
  ] },
  { group: "Other desks", items: [
    { to: "/tonight", label: "SPX session desk", icon: "LayoutDashboard", testId: "mc-back-to-spx", switcher: true },
    { to: "/equities", label: "Side experiments", icon: "ScanSearch", switcher: true },
    { to: "/pm", label: "Project", icon: "SquareKanban", min: "owner", hideLocked: true, switcher: true },
  ] },
  { group: "Help", items: [
    { to: "/guide", label: "Guide & tour", icon: "BookOpen", testId: "nav-guide" },
  ] },
];

const WORKSPACES = {
  spx: { nav: NAV, label: null },
  equities: { nav: EQ_NAV, label: "Equities" },
  pm: { nav: PM_NAV, label: "Project" },
  megacap: { nav: MC_NAV, label: "MegaCap" },
};

/** Items a role may see: hideLocked items vanish; others stay (locked) so the IA is discoverable. */
const visibleItems = (items, can) => items.filter((it) => !it.hidden && !(it.hideLocked && it.min && !can(it.min)));

const flatNav = (nav) => nav.flatMap((g) => g.items);
const screenFor = (nav, path) => {
  const items = flatNav(nav).filter((i) => (i.end ? path === i.to : path === i.to || path.startsWith(i.to + "/")));
  return items.sort((a, b) => b.to.length - a.to.length)[0];
};
const isTyping = (e) => {
  const t = e.target;
  return t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT" || t.isContentEditable);
};

/* ---------- Legal strip ---------- */
export const LEGAL_TEXT = ["Not financial advice", "Not an offer to buy or sell securities", "No orders are placed by this system"];
export function LegalStrip() {
  return (
    <div className="sn-legal" role="note" aria-label="Legal notice" data-testid="disclaimer-strip">
      <Icon name="Scale" />
      {LEGAL_TEXT.map((t, i) => (
        <React.Fragment key={t}>
          {i > 0 && <span className="sn-sep" aria-hidden="true">·</span>}
          <span>{t}</span>
        </React.Fragment>
      ))}
    </div>
  );
}

/* ---------- Rail ---------- */
function RailItem({ it, collapsed, locked, meta, onNavigate }) {
  const content = (
    <>
      <Icon name={it.icon} />
      <span className="sn-rail-label">{it.label}</span>
      <span className="sn-rail-meta">
        {locked && <Icon name="Lock" size={12} />}
        {meta}
      </span>
    </>
  );
  const testId = it.testId || `nav-${it.label.toLowerCase()}`;
  if (locked) {
    return (
      <span className="sn-rail-item" data-locked="true" aria-disabled="true" role="link" data-testid={testId}
        title={`${it.label} — requires the ${it.min} role or higher`}>
        {content}
      </span>
    );
  }
  return (
    <NavLink to={it.to} end={it.end} className="sn-rail-item" data-testid={testId} title={collapsed ? it.label : undefined} onClick={onNavigate}>
      {content}
    </NavLink>
  );
}

/** "Sign out everywhere": revokes every token for this user, after a confirmation. */
function useSignOutEverywhere() {
  const { logoutAll } = useAuth();
  const [open, setOpen] = useState(false);
  const dialog = open && (
    <ConfirmModal title="Sign out everywhere" icon="LogOut" confirmLabel="Sign out everywhere" onClose={() => setOpen(false)}
      onConfirm={async () => {
        try { await logoutAll(); } catch (e) { throw new Error(e?.response?.data?.detail?.message || (typeof e?.response?.data?.detail === "string" ? e.response.data.detail : e.message)); }
      }}>
      Ends your session on every browser and device, including this one. You will need to sign in again.
    </ConfirmModal>
  );
  return [() => setOpen(true), dialog];
}

function AccountMenu({ onClose, onSignOutAll }) {
  const { user, logout } = useAuth();
  const { theme, setTheme, density, setDensity } = usePrefs();
  const ref = useRef(null);
  useEffect(() => {
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [onClose]);
  return (
    <div className="sn-acct-menu" role="menu" aria-label="Account" ref={ref} data-testid="account-menu">
      <div className="sn-acct-label sn-small sn-faint" data-testid="current-user">{user?.email}</div>
      <div className="sn-acct-sep" />
      <div className="sn-acct-label sn-micro">Theme</div>
      {Object.entries(THEME_LABELS).map(([k, l]) => (
        <button key={k} type="button" role="menuitemradio" aria-checked={theme === k} className="sn-acct-item" onClick={() => setTheme(k)} data-testid={`theme-${k}`}>
          <Icon name={k === "light" ? "Sun" : k === "dark-hc" ? "Contrast" : "Moon"} />{l}{theme === k && <Icon name="Check" style={{ marginLeft: "auto" }} />}
        </button>
      ))}
      <div className="sn-acct-sep" />
      <div className="sn-acct-label sn-micro">Density</div>
      {["comfortable", "compact"].map((d) => (
        <button key={d} type="button" role="menuitemradio" aria-checked={density === d} className="sn-acct-item" onClick={() => setDensity(d)} data-testid={`density-${d}`}>
          <Icon name={d === "compact" ? "Rows4" : "Rows3"} />{d === "compact" ? "Compact" : "Comfortable"}{density === d && <Icon name="Check" style={{ marginLeft: "auto" }} />}
        </button>
      ))}
      <div className="sn-acct-sep" />
      <button type="button" role="menuitem" className="sn-acct-item" onClick={logout} data-testid="logout-btn">
        <Icon name="LogOut" />Sign out
      </button>
      <button type="button" role="menuitem" className="sn-acct-item" onClick={() => { onClose(); onSignOutAll(); }} data-testid="logout-all-btn">
        <Icon name="LogOut" />Sign out everywhere…
      </button>
    </div>
  );
}

function Rail({ nav, home = "/tonight", collapsed, workspaceLabel, provFlag, pmUnread }) {
  const { user, can } = useAuth();
  const { toggleRail } = usePrefs();
  const [closed, setClosed] = useState(() => {
    try { return JSON.parse(localStorage.getItem("sentinel_rail_closed") || "[]"); } catch { return []; }
  });
  const [menu, setMenu] = useState(false);
  const [signOutAll, signOutAllDialog] = useSignOutEverywhere();
  useEffect(() => { try { localStorage.setItem("sentinel_rail_closed", JSON.stringify(closed)); } catch { /* ignore */ } }, [closed]);
  const openPalette = () => window.dispatchEvent(new Event("sentinel:palette"));
  const name = user?.name || user?.email?.split("@")[0] || "Account";

  return (
    <nav className="sn-rail" data-collapsed={collapsed} aria-label="Primary" data-testid="main-nav">
      <div className="sn-rail-brand">
        <NavLink to={home} end aria-label={`Sentinel — ${workspaceLabel ? `${workspaceLabel} home` : "Tonight"}`} data-testid="brand" style={{ display: "inline-flex", alignItems: "center", gap: 8, color: "inherit", textDecoration: "none" }}>
          <LogoMark size={20} />
          {!collapsed && <span className="sn-rail-label" style={{ fontWeight: 600, fontSize: 15 }}>Sentinel</span>}
        </NavLink>
        {!collapsed && workspaceLabel && (
          <span className="sn-badge sn-rail-hide" style={{ borderColor: "var(--workspace-accent)", color: "var(--workspace-accent-fg)" }}>{workspaceLabel}</span>
        )}
        <IconButton icon={collapsed ? "PanelLeftOpen" : "PanelLeftClose"} label={collapsed ? "Expand navigation ( [ )" : "Collapse navigation ( [ )"} size="sm"
          onClick={toggleRail} style={collapsed ? { display: "none" } : { marginLeft: "auto" }} data-testid="rail-toggle" />
      </div>
      {!collapsed ? (
        <div className="sn-rail-search">
          <button type="button" className="sn-btn sn-btn-sm" aria-label="Open command palette (Ctrl K)" onClick={openPalette} data-testid="rail-search">
            <Icon name="Search" />Search or jump to…<Kbd>⌘K</Kbd>
          </button>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, padding: "8px 0 0" }}>
          <IconButton icon="PanelLeftOpen" label="Expand navigation ( [ )" onClick={toggleRail} data-testid="rail-toggle" />
          <IconButton icon="Search" label="Command palette (Ctrl K)" onClick={openPalette} />
        </div>
      )}
      <div className="sn-rail-nav">
        {nav.map((g) => {
          const items = visibleItems(g.items, can);
          if (!items.length) return null;
          const open = g.fixed || !closed.includes(g.group);
          return (
            <div key={g.group} className="sn-rail-group" data-open={open}>
              <button type="button" className="sn-rail-group-h" aria-expanded={open} disabled={g.fixed}
                style={g.fixed ? { cursor: "default" } : undefined}
                onClick={() => !g.fixed && setClosed((c) => (open ? [...c, g.group] : c.filter((x) => x !== g.group)))}>
                <span>{g.group}</span>{!g.fixed && <Icon name="ChevronDown" />}
              </button>
              {open && (
                <ul className="sn-rail-list">
                  {items.map((it) => (
                    <li key={it.to}>
                      <RailItem it={it} collapsed={collapsed} locked={it.min && !can(it.min)}
                        meta={
                          <>
                            {it.flag === "prov" && provFlag && <span className="sn-dot" style={{ color: "var(--provenance)" }} title="Data degraded" />}
                            {it.flag === "pm-unread" && pmUnread > 0 && <span className="sn-num" style={{ fontSize: 11, color: "var(--unread)" }} data-testid="pm-notif-badge">{pmUnread}</span>}
                          </>
                        } />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>
      <div className="sn-rail-foot">
        {menu && <AccountMenu onClose={() => setMenu(false)} onSignOutAll={signOutAll} />}
        {signOutAllDialog}
        <button type="button" className="sn-rail-user" aria-haspopup="menu" aria-expanded={menu} onClick={() => setMenu((m) => !m)}
          aria-label={`Account: ${name}, ${user?.role}`} data-testid="account-button">
          <Avatar name={name} />
          {!collapsed && (
            <span style={{ minWidth: 0, flex: 1 }} className="sn-rail-label">
              <span className="sn-rail-user-name" style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{name}</span>{user?.role && <RoleChip role={user.role} />}
              </span>
              <span className="sn-rail-user-mail" style={{ display: "block" }}>{user?.email}</span>
            </span>
          )}
          {!collapsed && <Icon name="ChevronsUpDown" size={14} style={{ color: "var(--text-tertiary)" }} />}
        </button>
        {!collapsed && <div className="sn-row sn-rail-hide" style={{ gap: 6 }}><VersionChip version={APP_VERSION} /></div>}
      </div>
    </nav>
  );
}

/* ---------- Status bar ---------- */
function DualClock({ now, compact }) {
  const et = timeET(now, { seconds: !compact });
  const syd = timeLocal(now, { seconds: !compact });
  const sydDay = dayLabel(now, localZone());
  const tz = localZoneAbbr(now);
  return (
    <span className="sn-row" style={{ gap: 12, flexWrap: "nowrap" }} aria-label={`Time ${et} Eastern, ${syd} ${tz} ${sydDay}`} data-testid="clocks">
      <span className="sn-clock"><span className="sn-clock-tz">ET</span><span className="sn-clock-t" data-testid="clock-et">{et}</span></span>
      <span className="sn-clock"><span className="sn-clock-tz">{tz}</span><span className="sn-clock-t" data-testid="clock-sydney">{syd}</span>{!compact && <span className="sn-clock-d">{sydDay}</span>}</span>
    </span>
  );
}
const MiniCheckpoints = ({ items }) => (
  <span className="sn-mini-cp" aria-hidden="true">{items.map((c) => <i key={c.id} data-s={c.s} />)}</span>
);

function AlertsButton({ size }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const { data } = useQuery({ queryKey: ["alerts"], queryFn: api.alerts, refetchInterval: 20000 });
  const items = data?.items || [];
  const unread = data?.unread || 0;
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [open]);
  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) {
      await api.readAlerts().catch(() => {});
      qc.invalidateQueries({ queryKey: ["alerts"] });
    }
  };
  const iconFor = (a) => ({ KILL_SWITCH: "Power", STALE: "Hourglass", ENTER: "CircleCheck", JOB_FAILED: "TriangleAlert" }[a.kind] || (a.severity === "high" ? "TriangleAlert" : "Bell"));
  return (
    <span className="sn-anchor" ref={ref}>
      <IconButton icon="Bell" label="Operator alerts" badge={unread} size={size} onClick={toggle} aria-expanded={open} data-testid="alerts-bell" />
      {unread > 0 && <span className="sn-vh" data-testid="alerts-unread">{unread}</span>}
      {open && (
        <div className="sn-pop sn-alerts" role="dialog" aria-label="Operator alerts" data-testid="alerts-panel">
          <div className="sn-alerts-h"><h3>Alerts</h3><span className="sn-num sn-faint" style={{ fontSize: 12 }}>{items.length} recent</span></div>
          {items.length === 0 ? (
            <p className="sn-small sn-muted" style={{ margin: 0, padding: "var(--space-4)" }}>
              No alerts. You'll be notified when the kill switch trips, a source goes stale, or an ENTER is sealed.
            </p>
          ) : (
            <ul className="sn-alerts-list" role="list">
              {items.slice(0, 40).map((a) => (
                <li key={a.id} className="sn-alert" data-testid={`alert-${a.kind}`}>
                  <Icon name={iconFor(a)} size={18} style={{ color: a.kind === "STALE" ? "var(--provenance)" : a.severity === "high" ? "var(--alarm-fg)" : "var(--text-secondary)" }} />
                  <span className="sn-alert-t">{String(a.kind || "").replace(/_/g, " ")}</span>
                  <span className="sn-alert-time">{a.session_date}{a.run ? ` · R${a.run}` : ""}</span>
                  <span className="sn-alert-b">{withLocalTimes(a.message, { day: a.session_date })}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </span>
  );
}

function SessionDate({ s, compact }) {
  const { selectedDate, setSelectedDate } = useAuth();
  const label = sessionDateLabel(selectedDate);
  return (
    <label className="sn-status-cell sn-status-date" aria-label={`Session date ${label}. Change date`} style={compact ? { border: 0, padding: 0 } : undefined}>
      {!compact && <Icon name="CalendarDays" style={{ color: "var(--text-tertiary)" }} />}
      <span className="sn-sans-num">{compact ? label.replace(/ \d{4}$/, "") : label}</span>
      {!compact && (
        <span className="sn-status-k sn-status-state" style={{ fontWeight: 400 }} data-testid="session-status" data-calendar-status={s.session?.status}>
          · {s.marketState}
        </span>
      )}
      {!compact && <Icon name="ChevronDown" size={14} style={{ color: "var(--text-tertiary)" }} />}
      <input type="date" value={selectedDate || ""} onChange={(e) => e.target.value && setSelectedDate(e.target.value)} data-testid="session-date-picker" aria-label="Session date" />
    </label>
  );
}

function NextRun({ s, now }) {
  if (!s.next) {
    return <span className="sn-cd-inline"><span className="sn-faint">{s.isToday ? "No further runs today" : "Archived session"}</span></span>;
  }
  const remaining = fmtDuration((s.next.at - now.getTime()) / 1000);
  return (
    <span className="sn-cd-inline" title={`${RUN_META[s.next.run].name} · ${etAndLocal(RUN_META[s.next.run].time)}`}>
      <span className="sn-faint">Next R{s.next.run} in</span>
      <span className="sn-cd-v sn-num" role="timer" aria-live="off" data-testid="countdown-value">{remaining}</span>
    </span>
  );
}

/** ENTER state for every role, from /health. "ENTER off" carries the owner's reason as a tooltip (title).
 *  An older backend sends no `enter`: nothing is drawn. */
function EnterChip({ cell }) {
  const { health } = useAuth();
  const enter = health?.enter;
  if (!enter) return null;
  const off = enter.in_effect === false;
  const label = off ? "ENTER off" : "ENTER on";
  const reason = enter.reason || (off ? "ENTER is switched off." : "ENTER is on.");
  const chip = (
    <span className={cx("sn-badge", off && "sn-prov")} title={reason} aria-label={`${label}. ${reason}`}
      data-testid="enter-status-chip" data-enter={off ? "off" : "on"}>
      <Icon name={off ? "PowerOff" : "Power"} />{label}
    </span>
  );
  return cell ? <div className="sn-status-cell">{chip}</div> : chip;
}

function StatusBar({ s, now }) {
  const { health } = useAuth();
  // The ENTER chip now says when ENTER is off; the old "ENTER disabled" note stays only for backends without it.
  const provDetail = s.provenance === "LIVE" ? "Massive.com · Options Advanced" : s.provenance === "STUB" && !health?.enter ? "ENTER disabled" : null;
  const openPalette = () => window.dispatchEvent(new Event("sentinel:palette"));
  const kill = s.killArmed
    ? <SystemBadge kind="kill-armed" data-testid="killswitch-badge" title={s.session?.kill_reason || undefined} />
    : <SystemBadge kind="kill-safe" />;
  return (
    <header className="sn-status" aria-label="Session status" data-testid="header-status-controls">
      <div className="sn-status-d">
        <SessionDate s={s} />
        <div className="sn-status-cell sn-status-run" aria-label={`Run ${s.currentRun} of 6`}>
          <span className="sn-status-k">Run</span><span className="sn-status-v">{s.currentRun}/6</span><MiniCheckpoints items={s.mini} />
        </div>
        <div className="sn-status-cell"><NextRun s={s} now={now} /></div>
        <div className="sn-status-cell">
          <span className="sn-status-k sn-status-opt-k">Data</span>
          <DataStateBadge state={s.provenance} data-testid={s.provenance === "LIVE" ? "live-banner" : "stub-banner"} />
          {provDetail && <span className="sn-small sn-faint sn-status-detail">{provDetail}</span>}
        </div>
        <div className={cx("sn-status-cell", !s.killArmed && "sn-status-killsafe")}>{kill}</div>
        <EnterChip cell />
        <div className="sn-status-cell sn-status-grow" />
        <div className="sn-status-cell"><DualClock now={now} /></div>
        <div className="sn-status-cell" style={{ padding: "0 8px", gap: 2 }}>
          <AlertsButton />
          <IconButton icon="Search" label="Command palette (Ctrl K)" onClick={openPalette} data-testid="palette-open" />
        </div>
      </div>
      <div className="sn-status-m">
        <SessionDate s={s} compact />
        <span className="sn-status-v sn-num" style={{ fontSize: 12 }}>R{s.currentRun}/6</span><MiniCheckpoints items={s.mini} />
        <span className="sn-sp" />
        <NextRun s={s} now={now} />
      </div>
      <div className="sn-status-m">
        <DataStateBadge state={s.provenance} />
        {s.killArmed ? <SystemBadge kind="kill-armed" /> : <SystemBadge kind="kill-safe">Kill safe</SystemBadge>}
        <EnterChip />
        <span className="sn-sp" />
        <DualClock now={now} compact />
      </div>
    </header>
  );
}

/* ---------- Command palette ---------- */
function CommandPalette({ nav, onClose }) {
  const navigate = useNavigate();
  const { can } = useAuth();
  const { cycleTheme, toggleDensity } = usePrefs();
  const [q, setQ] = useState("");
  const [act, setAct] = useState(0);
  const inputRef = useRef(null);
  const opener = useRef(document.activeElement);

  useEffect(() => {
    inputRef.current?.focus();
    const back = opener.current;
    return () => { if (back && back.focus) back.focus(); };
  }, []);

  const all = useMemo(() => {
    const visible = flatNav(nav).filter((i) => !i.min || can(i.min));
    const hidden = flatNav(nav).filter((i) => !i.hideLocked).length - visible.filter((i) => !i.hideLocked).length;
    return {
      hidden,
      groups: [
        { group: "Jump to", items: visible.filter((i) => !i.switcher).map((i) => ({ label: i.label, icon: i.icon, keys: i.key ? ["G", i.key] : null, run: () => navigate(i.to) })) },
        { group: "Switch desk", items: visible.filter((i) => i.switcher).map((i) => ({ label: i.label, icon: i.icon, keys: i.key ? ["G", i.key] : null, run: () => navigate(i.to) })) },
        { group: "Actions", items: [
          { label: "Print or save as PDF", icon: "Printer", keys: ["⌘", "P"], run: () => setTimeout(() => window.print(), 50) },
          { label: "Switch theme — Dark / High contrast / Paper", icon: "Contrast", keys: ["⌘", "⇧", "L"], run: cycleTheme },
          { label: "Toggle density — Comfortable / Compact", icon: "Rows3", keys: ["⌘", "⇧", "D"], run: toggleDensity },
          { label: "Change session date…", icon: "CalendarDays", keys: ["D"], run: () => setTimeout(() => document.querySelector('[data-testid="session-date-picker"]')?.showPicker?.(), 50) },
        ] },
      ],
    };
  }, [nav, can, navigate, cycleTheme, toggleDensity]);

  const groups = all.groups
    .map((g) => ({ ...g, items: g.items.filter((i) => i.label.toLowerCase().includes(q.toLowerCase())) }))
    .filter((g) => g.items.length);
  const flat = groups.flatMap((g) => g.items);
  const choose = (item) => { onClose(); item.run(); };
  let n = -1;

  return (
    <div className="sn-scrim" onMouseDown={(e) => e.target === e.currentTarget && onClose()} data-testid="command-palette">
      <div className="sn-cmdk" role="dialog" aria-modal="true" aria-label="Command palette">
        <div className="sn-cmdk-in">
          <Icon name="Search" style={{ color: "var(--text-tertiary)" }} />
          <input ref={inputRef} value={q} placeholder="Jump to a screen or run an action…" role="combobox" aria-expanded="true"
            aria-controls="sn-cmdk-l" aria-activedescendant={"sn-cmdk-" + act}
            onChange={(e) => { setQ(e.target.value); setAct(0); }}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") { e.preventDefault(); setAct((a) => Math.min(flat.length - 1, a + 1)); }
              if (e.key === "ArrowUp") { e.preventDefault(); setAct((a) => Math.max(0, a - 1)); }
              if (e.key === "Enter" && flat[act]) { e.preventDefault(); choose(flat[act]); }
              if (e.key === "Escape") { e.preventDefault(); onClose(); }
            }} />
          <Kbd>Esc</Kbd>
        </div>
        <ul className="sn-cmdk-list" id="sn-cmdk-l" role="listbox">
          {groups.map((g) => (
            <li key={g.group} role="presentation">
              <div className="sn-cmdk-group" role="presentation">{g.group}</div>
              <ul role="group" aria-label={g.group} style={{ listStyle: "none", margin: 0, padding: 0 }}>
                {g.items.map((i) => {
                  n++;
                  const k = n;
                  return (
                    <li key={i.label} id={"sn-cmdk-" + k} role="option" aria-selected={k === act} className="sn-cmdk-item"
                      onMouseEnter={() => setAct(k)} onClick={() => choose(i)}>
                      <Icon name={i.icon} />{i.label}
                      {i.keys && <span className="sn-cmdk-keys">{i.keys.map((x) => <Kbd key={x}>{x}</Kbd>)}</span>}
                    </li>
                  );
                })}
              </ul>
            </li>
          ))}
          {!flat.length && <li className="sn-cmdk-item" aria-disabled="true" style={{ color: "var(--text-tertiary)" }}>No matches. Try a screen name such as Checkpoints.</li>}
        </ul>
        <div className="sn-cmdk-f">
          <span><Kbd>↑</Kbd><Kbd>↓</Kbd> move</span><span><Kbd>↵</Kbd> open</span><span><Kbd>Esc</Kbd> close</span>
          {all.hidden > 0 && <span style={{ marginLeft: "auto" }}>{all.hidden} hidden for your role</span>}
        </div>
      </div>
    </div>
  );
}

/* ---------- Mobile ---------- */
function TopBar({ title, onMore, home = "/tonight" }) {
  const { user } = useAuth();
  const openPalette = () => window.dispatchEvent(new Event("sentinel:palette"));
  return (
    <div className="sn-topbar">
      <NavLink to={home} end aria-label="Sentinel — home" data-testid="brand" style={{ display: "inline-flex" }}><LogoMark size={20} /></NavLink>
      <span className="sn-topbar-title">{title}</span>
      <span className="sn-sp" />
      <IconButton icon="Search" label="Search" size="lg" onClick={openPalette} />
      <AlertsButton size="lg" />
      <button type="button" className="sn-btn sn-btn-ghost sn-btn-icon sn-btn-lg" aria-label="Account and more" onClick={onMore}>
        <Avatar name={user?.name || user?.email || ""} size="sm" />
      </button>
    </div>
  );
}

const TAB_ITEMS = {
  spx: [["/tonight", "Tonight", "LayoutDashboard"], ["/checkpoints", "Runs", "GitCommitVertical"], ["/history", "History", "History"], ["/scoring", "Scoring", "Target"]],
  equities: [["/equities/cef", "Funds", "Scale"], ["/equities/spacs", "SPACs", "Hourglass"], ["/equities/tenders", "Tenders", "FileText"], ["/equities/track-record", "Record", "History"]],
  megacap: [["/megacap", "Register", "ListChecks", true], ["/megacap/chapters", "Map", "BookOpen"]],
};
const tabActive = (path, to, exact) => (exact ? path === to : path === to || path.startsWith(to + "/"));
function MobileTabBar({ items, label, path, onMore, moreOpen }) {
  const navigate = useNavigate();
  const inTabs = items.some(([to, , , exact]) => tabActive(path, to, exact));
  return (
    <nav className="sn-tabbar" aria-label={label}>
      {items.map(([to, l, ic, exact]) => (
        <button key={to} type="button" className="sn-tabbar-item" aria-current={tabActive(path, to, exact) ? "page" : undefined} onClick={() => navigate(to)} data-testid={`tab-${l.toLowerCase()}`}>
          <Icon name={ic} />{l}
        </button>
      ))}
      <button type="button" className="sn-tabbar-item" aria-current={!inTabs || moreOpen ? "page" : undefined} aria-expanded={moreOpen} onClick={onMore} data-testid="tab-more">
        <Icon name="Menu" />More
      </button>
    </nav>
  );
}

/** `hide`: paths already in the tab bar; the sheet lists every other destination. */
function MoreSheet({ nav, hide, onClose }) {
  const { can, user, logout } = useAuth();
  const { theme, setTheme, density, setDensity } = usePrefs();
  const [signOutAll, signOutAllDialog] = useSignOutEverywhere();
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="sn-more" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="sn-more-panel" role="dialog" aria-modal="true" aria-label="More">
        {nav.map((g) => ({ ...g, items: visibleItems(g.items, can).filter((it) => !hide.has(it.to)) })).filter((g) => g.items.length).map((g) => (
          <div key={g.group} className="sn-rail-group">
            <div className="sn-rail-group-h" style={{ cursor: "default" }}><span>{g.group}</span></div>
            <ul className="sn-rail-list">
              {g.items.map((it) => <li key={it.to}><RailItem it={it} locked={it.min && !can(it.min)} onNavigate={onClose} /></li>)}
            </ul>
          </div>
        ))}
        <div className="sn-rail-group">
          <div className="sn-rail-group-h" style={{ cursor: "default" }}><span>Account</span></div>
          <div className="sn-stack" style={{ gap: 8, padding: "0 var(--space-2)" }}>
            <span className="sn-small sn-muted">{user?.email} · {user?.role}</span>
            <div className="sn-row">
              {Object.entries(THEME_LABELS).map(([k, l]) => (
                <button key={k} type="button" className="sn-fchip" aria-pressed={theme === k} onClick={() => setTheme(k)}>{l}</button>
              ))}
            </div>
            <div className="sn-row">
              {["comfortable", "compact"].map((d) => (
                <button key={d} type="button" className="sn-fchip" aria-pressed={density === d} onClick={() => setDensity(d)}>{d === "compact" ? "Compact" : "Comfortable"}</button>
              ))}
            </div>
            <button type="button" className="sn-btn sn-btn-secondary" style={{ minHeight: 44 }} onClick={logout} data-testid="logout-btn"><Icon name="LogOut" />Sign out</button>
            <button type="button" className="sn-btn sn-btn-ghost" style={{ minHeight: 44 }} onClick={signOutAll} data-testid="logout-all-btn"><Icon name="LogOut" />Sign out everywhere…</button>
            {signOutAllDialog}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- Equities status bar ---------- */
const etParts = (now) => {
  const p = new Intl.DateTimeFormat("en-CA", { timeZone: "America/New_York", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", weekday: "short", hourCycle: "h23" }).formatToParts(now);
  const g = (t) => p.find((x) => x.type === t)?.value;
  return { date: `${g("year")}-${g("month")}-${g("day")}`, minutes: Number(g("hour")) * 60 + Number(g("minute")), weekday: g("weekday") };
};
const toMin = (hhmm) => {
  const m = /^(\d{2}):(\d{2})$/.exec(hhmm || "");
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};

/** US cash-equity session state in ET. Uses the trading calendar when the API has it; weekday and clock otherwise. */
export function useUsMarketState(now) {
  const { date, minutes, weekday } = etParts(now);
  const { data: cal } = useQuery({ queryKey: ["market-calendar", date], queryFn: () => api.marketCalendar(date), retry: false, staleTime: 60 * 60 * 1000 });
  const close = toMin(cal?.close_et) ?? 960;
  let state = "Closed";
  if (cal?.holiday) state = "Holiday";
  else if (cal?.is_trading_day === false || weekday === "Sat" || weekday === "Sun") state = "Closed";
  else if (minutes >= 240 && minutes < 570) state = "Pre-market";
  else if (minutes >= 570 && minutes < close) state = "Open";
  else if (minutes >= close && minutes < 1200) state = "After hours";
  return { state, open: state === "Open", holiday: cal?.holiday || null, earlyClose: !!cal?.early_close, closeET: cal?.close_et || "16:00" };
}

function EqStatusBar({ now, label: wsLabel = "Side experiments", icon = "ScanSearch", note = "Paper records only · separate from the SPX decision desk", testId = "eq-status" }) {
  const m = useUsMarketState(now);
  const openPalette = () => window.dispatchEvent(new Event("sentinel:palette"));
  const label = m.holiday ? `Holiday · ${m.holiday}` : m.state;
  return (
    <header className="sn-status" aria-label={`${wsLabel} status`} data-testid={testId}>
      <div className="sn-status-d">
        <div className="sn-status-cell">
          <Icon name={icon} style={{ color: "var(--workspace-accent-fg)" }} />
          <span style={{ fontWeight: 500, color: "var(--text-primary)" }}>{wsLabel}</span>
        </div>
        <div className="sn-status-cell">
          <span className="sn-status-k">US market</span>
          <span className="sn-status-v" data-testid="eq-market-state">{label}</span>
          {m.earlyClose && !m.holiday && <SystemBadge kind="early-close">EARLY CLOSE {etAndLocal(m.closeET)}</SystemBadge>}
        </div>
        <div className="sn-status-cell"><span className="sn-small sn-faint sn-status-detail">{note}</span></div>
        <div className="sn-status-cell sn-status-grow" />
        <div className="sn-status-cell"><DualClock now={now} /></div>
        <div className="sn-status-cell" style={{ padding: "0 8px", gap: 2 }}>
          <IconButton icon="Search" label="Command palette (Ctrl K)" onClick={openPalette} data-testid="palette-open" />
        </div>
      </div>
      <div className="sn-status-m">
        <span className="sn-status-v" style={{ fontSize: 12 }}>US market · {label}</span>
        <span className="sn-sp" />
        <DualClock now={now} compact />
      </div>
    </header>
  );
}

/* ---------- Shell ---------- */
/**
 * The app frame: legal strip, rail (desktop) or top bar + tab bar (mobile), status bar, page outlet.
 * `workspace` is "spx" (default), "equities", "megacap" or "pm"; each has its own rail and accent (data-workspace).
 * `denied` names a role: the outlet is replaced by an access-denied state (a workspace the role cannot open).
 */
export default function AppShell({ workspace = "spx", denied }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { can, user } = useAuth();
  const { railCollapsed, toggleRail, cycleTheme, toggleDensity } = usePrefs();
  const { mobile, narrow } = useViewport();
  const now = useNow(1000);
  const s = useSessionState(now);
  const [palette, setPalette] = useState(false);
  const [more, setMore] = useState(false);
  const ws = WORKSPACES[workspace] || WORKSPACES.spx;
  const nav = ws.nav;
  const home = workspace === "equities" ? "/equities" : workspace === "pm" ? "/pm" : workspace === "megacap" ? "/megacap" : "/tonight";
  const screen = screenFor(nav, location.pathname);
  const { data: pmNotif } = useQuery({ queryKey: ["pm-notifications"], queryFn: api.pmNotifications, refetchInterval: 20000, enabled: workspace === "pm" && !denied });

  useEffect(() => {
    // Screens outside the rail (an equities symbol, a deep link) set their own title.
    if (screen) document.title = `Sentinel — ${screen.label}`;
  }, [screen]);
  useEffect(() => { setMore(false); }, [location.pathname]);

  // Keyboard: ⌘K palette, G-then-letter navigation, [ rail, ⌘⇧L theme, ⌘⇧D density, ? guide, D date.
  useEffect(() => {
    let g = false;
    let gTimer;
    const onPalette = () => setPalette(true);
    const onKey = (e) => {
      const mod = e.metaKey || e.ctrlKey;
      if (mod && e.key.toLowerCase() === "k") { e.preventDefault(); setPalette((p) => !p); return; }
      if (mod && e.shiftKey && e.key.toLowerCase() === "l") { e.preventDefault(); cycleTheme(); return; }
      if (mod && e.shiftKey && e.key.toLowerCase() === "d") { e.preventDefault(); toggleDensity(); return; }
      if (mod || e.altKey || isTyping(e)) return;
      if (g) {
        g = false;
        const hit = flatNav(NAV).find((i) => i.key && i.key.toLowerCase() === e.key.toLowerCase());
        if (hit && (!hit.min || can(hit.min))) { e.preventDefault(); navigate(hit.to); }
        return;
      }
      if (e.key === "g") { g = true; clearTimeout(gTimer); gTimer = setTimeout(() => { g = false; }, 1200); return; }
      if (e.key === "[") { toggleRail(); return; }
      if (e.key === "?") { navigate("/guide"); return; }
      if (e.key === "d") { document.querySelector('[data-testid="session-date-picker"]')?.showPicker?.(); }
    };
    window.addEventListener("sentinel:palette", onPalette);
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("sentinel:palette", onPalette); window.removeEventListener("keydown", onKey); clearTimeout(gTimer); };
  }, [navigate, can, toggleRail, cycleTheme, toggleDensity]);

  const collapsed = railCollapsed || (narrow && !mobile);
  const page = (
    <main className={cx("sn-page", workspace === "pm" && "sn-page-pm")} id="main">
      <ErrorBoundary key={location.pathname}>
        {denied ? (
          <EmptyState icon="Lock" title="Access denied" data-testid="access-denied">
            This workspace requires the {denied} role. Your role is {user?.role || "viewer"}.
          </EmptyState>
        ) : (
          <Outlet context={{ session: s.session, sessionState: s }} />
        )}
      </ErrorBoundary>
    </main>
  );
  const status = workspace === "equities" ? <EqStatusBar now={now} />
    : workspace === "megacap" ? <EqStatusBar now={now} label="MegaCap playbook" icon="Layers" testId="mc-status"
        note="Research stream · separate from the 10:00 am programme" />
    : <StatusBar s={s} now={now} />;
  const tabs = TAB_ITEMS[workspace];

  return (
    <div className="sn-app sn-root" data-viewport={mobile ? "mobile" : "desktop"} data-workspace={workspace === "spx" ? undefined : workspace}
      data-testid={workspace === "pm" ? "pm-app" : workspace === "equities" ? "equities-app" : workspace === "megacap" ? "megacap-app" : "app-shell"}>
      <LegalStrip />
      {mobile ? (
        <>
          <TopBar title={screen?.label || ws.label || "Sentinel"} onMore={() => setMore(true)} home={home} />
          {status}
          {page}
          {tabs && <MobileTabBar items={tabs} label={ws.label || "Session"} path={location.pathname} onMore={() => setMore((m) => !m)} moreOpen={more} />}
          {more && <MoreSheet nav={nav} hide={new Set((tabs || []).map(([to]) => to))} onClose={() => setMore(false)} />}
        </>
      ) : (
        <div className="sn-app-body">
          <Rail nav={nav} home={home} collapsed={collapsed} workspaceLabel={ws.label}
            provFlag={s.provenance !== "LIVE"} pmUnread={pmNotif?.unread || 0} />
          <div className="sn-app-main">
            {status}
            {page}
          </div>
        </div>
      )}
      {palette && <CommandPalette nav={nav} onClose={() => setPalette(false)} />}
    </div>
  );
}
